import { canEdit } from '../store.js';
import { updateInstanceStopCoords, updateInstanceBaseCoords } from '../instance-itinerary.js';
import { patchBaseCoordsInContext, patchStopCoordsInContext } from '../trip-context.js';
import { createMapPanel } from '../map/map-panel.js';
import { coordsOnly, hasValidCoords, resolveCoordinatesFast } from '../utils/map-coords.js';
import { enqueueGeocode } from '../utils/geocode-queue.js';
import { fetchDrivingRoute } from '../utils/map-route.js';
import { loadPointsSnapshot, savePointsSnapshot } from '../utils/map-cache.js';
import { toDayKey } from '../utils/day-key.js';
import { formatDateIT } from '../utils/format.js';
import { escapeHtml } from '../utils/dom.js';
import { resolveTripBases } from '../utils/trip-bases.js';
import { bindTripShell, getTripMainEl, renderTripShell } from './shell.js';

let mapPanel = null;
let paintThrottleTimer = null;
let lastPaintAt = 0;
let mapShellTripId = null;

let mapState = {
  instanceId: null,
  tripId: null,
  dayKey: null,
  selectedId: null,
  points: [],
  routeInfo: null,
  loading: false,
  geocoding: false,
  progress: { done: 0, total: 0, failed: 0, phase: 'idle' },
};

function resetMapPanel() {
  mapPanel?.destroy();
  mapPanel = null;
}

function filterDays(days, dayKey) {
  if (!dayKey) return days;
  return days.filter((d) => toDayKey(d.date) === dayKey);
}

function entityUpdatedAt(entity) {
  return entity.updated_at ?? entity.updatedAt ?? null;
}

function resolveFast(entity, cacheOpts) {
  return coordsOnly(resolveCoordinatesFast(entity, cacheOpts));
}

async function persistCoords(ctx, point, coords) {
  const entity = point._entity;
  if (!hasValidCoords(coords) || !canEdit() || !navigator.onLine) return;

  if (point._persist === 'stop' && !entity.lat && !entity.lng) {
    await updateInstanceStopCoords(ctx.instance.id, entity.id, coords).catch(() => {});
    patchStopCoordsInContext(entity.id, coords);
  }
  if (point._persist === 'base' && !entity.lat && !entity.lng) {
    await updateInstanceBaseCoords(ctx.instance.id, entity.id, coords).catch(() => {});
    patchBaseCoordsInContext(entity.id, coords);
  }
}

function buildPointSkeleton(ctx, dayKey) {
  const days = filterDays(ctx.days, dayKey);
  const points = [];
  const pending = [];
  let stopCounter = 0;

  for (const day of days) {
    const dayLabel = formatDateIT(day.date);
    for (const stop of day.stops ?? []) {
      stopCounter += 1;
      const cacheOpts = {
        instanceId: ctx.instance.id,
        entityType: 'stop',
        entityId: stop.id,
        entityUpdatedAt: entityUpdatedAt(stop),
      };
      const coords = resolveFast(stop, cacheOpts);
      const point = {
        id: `stop-${stop.id}`,
        entityId: stop.id,
        entityType: 'stop',
        kind: 'stop',
        index: stopCounter,
        name: stop.name,
        dayKey: toDayKey(day.date),
        dayTitle: day.title,
        dayLabel,
        mapsUrl: stop.maps_url ?? stop.mapsUrl,
        coords: coords ?? null,
        failed: false,
        _entity: stop,
        _persist: 'stop',
        _cacheOpts: cacheOpts,
        _priority: dayKey ? 10 : 5,
      };
      points.push(point);
      if (!coords) pending.push(point);
      else persistCoords(ctx, point, coords);
    }
  }

  if (!dayKey) {
    for (const item of ctx.live.wishlist ?? []) {
      if (item.showOnMap === false) continue;
      const cacheOpts = {
        instanceId: ctx.instance.id,
        entityType: 'wish',
        entityId: item.id,
        entityUpdatedAt: entityUpdatedAt(item),
      };
      const entity = {
        name: item.name,
        mapsUrl: item.mapsUrl,
        maps_url: item.mapsUrl,
        lat: item.lat,
        lng: item.lng,
        place_meta: item.placeMeta,
      };
      const coords = resolveFast(entity, cacheOpts);
      const point = {
        id: `wish-${item.id}`,
        entityId: item.id,
        entityType: 'wishlist',
        kind: 'wishlist',
        name: item.name,
        notes: item.notes,
        mapsUrl: item.mapsUrl,
        coords: coords ?? null,
        failed: false,
        _entity: entity,
        _persist: null,
        _cacheOpts: cacheOpts,
        _priority: 1,
      };
      points.push(point);
      if (!coords) pending.push(point);
    }

    for (const base of resolveTripBases(ctx.live)) {
      const cacheOpts = {
        instanceId: ctx.instance.id,
        entityType: 'base',
        entityId: base.id,
        entityUpdatedAt: entityUpdatedAt(base),
      };
      const entity = {
        name: base.name,
        mapsUrl: base.mapsUrl,
        lat: base.lat,
        lng: base.lng,
      };
      const coords = resolveFast(entity, cacheOpts);
      const point = {
        id: `base-${base.id}`,
        entityId: base.id,
        entityType: 'base',
        kind: 'base',
        name: base.name,
        subtitle: base.subtitle,
        mapsUrl: base.mapsUrl,
        coords: coords ?? null,
        failed: false,
        _entity: entity,
        _persist: 'base',
        _cacheOpts: cacheOpts,
        _priority: 2,
      };
      points.push(point);
      if (!coords) pending.push(point);
      else persistCoords(ctx, point, coords);
    }
  }

  return { points, pending };
}

function computeProgress(points) {
  const total = points.length;
  const done = points.filter((p) => hasValidCoords(p.coords)).length;
  const failed = points.filter((p) => p.failed).length;
  return { done, total, failed };
}

async function geocodePending(ctx, pending, onProgress) {
  if (!pending.length) return;

  mapState.geocoding = true;
  const country = ctx.trip.country ?? '';

  await Promise.all(
    pending.map((point) => {
      const entity = point._entity;
      return enqueueGeocode(
        {
          id: point.id,
          entity: {
            name: entity.name,
            maps_url: entity.maps_url ?? entity.mapsUrl,
            mapsUrl: entity.mapsUrl,
            lat: entity.lat,
            lng: entity.lng,
            place_meta: entity.place_meta ?? entity.placeMeta,
            placeMeta: entity.placeMeta,
          },
          country,
          instanceId: ctx.instance.id,
          entityType: point._cacheOpts.entityType,
          entityId: point._cacheOpts.entityId,
          entityUpdatedAt: point._cacheOpts.entityUpdatedAt,
        },
        point._priority ?? 1,
      ).then(async (result) => {
        const coords = coordsOnly(result);
        point.coords = coords;
        point.failed = !hasValidCoords(coords);
        if (hasValidCoords(coords)) {
          await persistCoords(ctx, point, coords);
        }
        mapState.progress = { ...computeProgress(mapState.points), phase: 'geocoding' };
        onProgress?.();
      });
    }),
  );

  mapState.geocoding = false;
}

async function buildMapPoints(ctx, dayKey, onProgress) {
  const snapshot = loadPointsSnapshot(ctx.instance.id, dayKey);
  if (snapshot?.length) {
    mapState.progress = {
      done: snapshot.filter((p) => hasValidCoords(p.coords)).length,
      total: snapshot.length,
      failed: snapshot.filter((p) => p.failed).length,
      phase: 'loading',
    };
    onProgress?.();
  }

  const { points, pending } = buildPointSkeleton(ctx, dayKey);
  mapState.progress = { ...computeProgress(points), phase: pending.length ? 'geocoding' : 'done' };
  onProgress?.();

  await geocodePending(ctx, pending, onProgress);

  const cleaned = points.map(({ _entity, _persist, _cacheOpts, _priority, ...rest }) => rest);
  savePointsSnapshot(ctx.instance.id, dayKey, cleaned);
  return cleaned;
}

function renderDayFilters(ctx, activeDayKey) {
  const allActive = !activeDayKey ? ' map-filter__chip--active' : '';
  const chips = [
    `<button type="button" class="map-filter__chip${allActive}" data-day="all">Tutti</button>`,
  ];

  for (const day of ctx.days) {
    const key = toDayKey(day.date);
    const active = activeDayKey === key ? ' map-filter__chip--active' : '';
    chips.push(
      `<button type="button" class="map-filter__chip${active}" data-day="${key}">${formatDateIT(key)}</button>`,
    );
  }

  return `<div class="map-filter" role="tablist" aria-label="Filtra per giorno">${chips.join('')}</div>`;
}

function renderRouteStats(routeInfo) {
  if (!routeInfo?.distance && !routeInfo?.duration) return '';
  const parts = [];
  if (routeInfo.duration) parts.push(`🚗 ${routeInfo.duration}`);
  if (routeInfo.distance) parts.push(routeInfo.distance);
  return `<p class="map-route-stats">${parts.join(' · ')}</p>`;
}

function renderMapStatus(progress, loading, geocoding, tripId) {
  const { done, total, failed, phase } = progress;
  if (loading && total === 0) {
    return `<p class="map-status map-status--loading">Recupero posizioni in corso…</p>`;
  }
  if (geocoding) {
    return `<p class="map-status map-status--loading">Caricamento luoghi… ${done}/${total}</p>`;
  }
  if (phase === 'done' && failed > 0) {
    return `<p class="map-status map-status--warn">${done}/${total} luoghi posizionati · ${failed} non trovati · <a href="#/trip/${tripId}/customize/days">Personalizza</a></p>`;
  }
  if (phase === 'done' && total > 0 && done === total) {
    return `<p class="map-status map-status--ok">${done} luoghi sulla mappa</p>`;
  }
  return '';
}

function renderStopList(points, selectedId, tripId) {
  const stops = points.filter((p) => p.kind === 'stop');
  if (!stops.length) {
    return `<p class="map-list__empty">Nessuna tappa per questo filtro.</p>`;
  }

  return `
    <ul class="map-list">
      ${stops
        .map((p) => {
          const active = p.id === selectedId ? ' map-list__item--active' : '';
          const meta = p.dayLabel ? `<span class="map-list__meta">${escapeHtml(p.dayLabel)}</span>` : '';
          const missing = !hasValidCoords(p.coords)
            ? `<span class="map-list__missing">Posizione non trovata · <a class="map-list__fix" href="#/trip/${tripId}/customize/days">Aggiungi</a></span>`
            : '';
          return `
            <li>
              <button type="button" class="map-list__item${active}" data-point-id="${escapeHtml(p.id)}">
                <span class="map-list__num">${p.index}</span>
                <span class="map-list__body">
                  <span class="map-list__name">${escapeHtml(p.name)}</span>
                  ${meta}
                  ${missing}
                </span>
              </button>
            </li>
          `;
        })
        .join('')}
    </ul>
  `;
}

function loaderIsActive(progress, loading, geocoding) {
  if (loading && progress.total === 0) return true;
  return geocoding;
}

function renderMapLoader(progress, loading, geocoding) {
  const active = loaderIsActive(progress, loading, geocoding);
  const { done, total } = progress;
  const label =
    loading && total === 0
      ? 'Preparazione mappa…'
      : geocoding
        ? `Caricamento luoghi… ${done}/${total}`
        : 'Mappa pronta';

  return `
    <div class="map-view__loader${active ? ' map-view__loader--active' : ''}" role="status" aria-live="polite">
      <div class="map-view__spinner" aria-hidden="true"></div>
      <p class="map-view__loader-text">${escapeHtml(label)}</p>
    </div>
  `;
}

function renderMapContent(ctx) {
  const { dayKey, selectedId, points, routeInfo, loading, geocoding, progress } = mapState;

  return `
    <div class="map-view">
      ${renderDayFilters(ctx, dayKey)}
      ${renderMapStatus(progress, loading, geocoding, ctx.tripId)}
      ${renderRouteStats(routeInfo)}
      <div class="map-view__canvas-wrap">
        <div id="trip-map-canvas" class="map-view__canvas" aria-label="Mappa itinerario"></div>
        ${renderMapLoader(progress, loading, geocoding)}
      </div>
      <section class="map-list-panel" aria-label="Tappe">
        <h2 class="map-list-panel__title">Tappe${dayKey ? ` · ${formatDateIT(dayKey)}` : ''}</h2>
        ${loading && points.length === 0 ? '<p class="map-list__empty">Recupero posizioni…</p>' : renderStopList(points, selectedId, ctx.tripId)}
      </section>
    </div>
  `;
}

function markersFromPoints(points, selectedId) {
  return points
    .filter((p) => hasValidCoords(p.coords))
    .map((p) => ({
      id: p.id,
      lat: p.coords.lat,
      lng: p.coords.lng,
      label: p.name,
      kind: p.kind,
      index: p.index,
      active: p.id === selectedId,
    }));
}

function updateMapDom(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;

  const { done, total, failed, phase } = mapState.progress;
  const { loading, geocoding } = mapState;

  const loader = main.querySelector('.map-view__loader');
  const loaderText = main.querySelector('.map-view__loader-text');
  if (loader) {
    loader.classList.toggle('map-view__loader--active', loaderIsActive(mapState.progress, loading, geocoding));
  }
  if (loaderText) {
    loaderText.textContent =
      loading && total === 0
        ? 'Preparazione mappa…'
        : geocoding
          ? `Caricamento luoghi… ${done}/${total}`
          : 'Mappa pronta';
  }

  const statusEl = main.querySelector('.map-status');
  const statusHtml = renderMapStatus(mapState.progress, loading, geocoding, ctx.tripId);
  if (statusEl) statusEl.outerHTML = statusHtml;
  else if (statusHtml) {
    main.querySelector('.map-filter')?.insertAdjacentHTML('afterend', statusHtml);
  }

  main.querySelectorAll('.map-filter__chip').forEach((chip) => {
    const key = chip.dataset.day;
    const activeDay = mapState.dayKey;
    const isActive = key === 'all' ? !activeDay : key === activeDay;
    chip.classList.toggle('map-filter__chip--active', isActive);
  });

  const listPanel = main.querySelector('.map-list-panel');
  if (listPanel) {
    listPanel.innerHTML = `
      <h2 class="map-list-panel__title">Tappe${mapState.dayKey ? ` · ${formatDateIT(mapState.dayKey)}` : ''}</h2>
      ${loading && mapState.points.length === 0 ? '<p class="map-list__empty">Recupero posizioni…</p>' : renderStopList(mapState.points, mapState.selectedId, ctx.tripId)}
    `;
    bindMapList(container, ctx);
  }

  if (import.meta.env.DEV && phase === 'done') {
    console.debug('[map]', { done, total, failed, dayKey: mapState.dayKey });
  }
}

function paintMap(ctx, { fit = false, force = false } = {}) {
  const canvas = document.getElementById('trip-map-canvas');
  if (!canvas) return;

  const runPaint = () => {
    if (!mapPanel) {
      mapPanel = createMapPanel(canvas);
    }

    mapPanel.ensureMap();
    const markers = markersFromPoints(mapState.points, mapState.selectedId);
    mapPanel.syncMarkers(markers, (id) => {
      mapState.selectedId = id;
      paintMap(ctx, { fit: false, force: true });
      const point = mapState.points.find((p) => p.id === id);
      if (point && hasValidCoords(point.coords)) {
        mapPanel.flyTo(point.coords.lat, point.coords.lng);
      }
      scrollToListItem(id);
    });

    if (mapState.routeInfo?.polyline?.length >= 2) {
      mapPanel.setRoute(mapState.routeInfo.polyline);
    } else {
      mapPanel.setRoute([]);
    }

    if (fit) {
      mapPanel.fitToMarkersDebounced(mapState.geocoding ? 350 : 0);
    }

    requestAnimationFrame(() => mapPanel.invalidateSize());
  };

  if (force || !mapState.geocoding) {
    lastPaintAt = Date.now();
    runPaint();
    return;
  }

  const elapsed = Date.now() - lastPaintAt;
  if (elapsed >= 250) {
    lastPaintAt = Date.now();
    runPaint();
    return;
  }

  if (paintThrottleTimer) return;
  paintThrottleTimer = setTimeout(() => {
    paintThrottleTimer = null;
    lastPaintAt = Date.now();
    runPaint();
  }, 250 - elapsed);
}

async function refreshMapData(ctx, container, { fit = true } = {}) {
  mapState.loading = true;
  mapState.geocoding = false;
  mapState.progress = { done: 0, total: 0, failed: 0, phase: 'loading' };
  updateMapDom(container, ctx);
  paintMap(ctx, { fit: false, force: true });

  mapState.points = await buildMapPoints(ctx, mapState.dayKey, () => {
    updateMapDom(container, ctx);
    paintMap(ctx, { fit: false });
  });

  const stopCoords = mapState.points
    .filter((p) => p.kind === 'stop' && hasValidCoords(p.coords))
    .map((p) => p.coords);

  if (stopCoords.length >= 2 && mapState.dayKey) {
    mapState.routeInfo = await fetchDrivingRoute(stopCoords, {
      instanceId: ctx.instance.id,
      dayKey: mapState.dayKey,
    });
  } else {
    mapState.routeInfo = null;
  }

  mapState.loading = false;
  mapState.progress = {
    ...computeProgress(mapState.points),
    phase: 'done',
  };
  updateMapDom(container, ctx);

  const main = getTripMainEl(container);
  const statsEl = main?.querySelector('.map-route-stats');
  if (main) {
    const statsHtml = renderRouteStats(mapState.routeInfo);
    if (statsEl) statsEl.outerHTML = statsHtml || '';
    else if (statsHtml) {
      const anchor = main.querySelector('.map-status') ?? main.querySelector('.map-filter');
      anchor?.insertAdjacentHTML('afterend', statsHtml);
    }
  }

  paintMap(ctx, { fit, force: true });
}

function scrollToListItem(id) {
  document.querySelector(`.map-list__item[data-point-id="${CSS.escape(id)}"]`)?.scrollIntoView({
    block: 'nearest',
    behavior: 'smooth',
  });
}

function navigateMapDay(tripId, dayKey) {
  if (!dayKey || dayKey === 'all') {
    location.hash = `#/trip/${tripId}/map/all`;
    return;
  }
  location.hash = `#/trip/${tripId}/map/${dayKey}`;
}

function bindMapList(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;

  main.querySelectorAll('.map-list__item').forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => {
      const id = btn.dataset.pointId;
      mapState.selectedId = id;
      paintMap(ctx, { force: true });
      const point = mapState.points.find((p) => p.id === id);
      if (point && hasValidCoords(point.coords)) {
        mapPanel?.flyTo(point.coords.lat, point.coords.lng);
      }
    });
  });
}

function bindMapView(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;

  if (!main.dataset.mapFiltersBound) {
    main.dataset.mapFiltersBound = '1';
    main.querySelectorAll('.map-filter__chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        const day = chip.dataset.day;
        navigateMapDay(ctx.tripId, day === 'all' ? null : day);
      });
    });
  }

  bindMapList(container, ctx);
}

function initMapState(ctx, dayKey) {
  if (mapState.tripId !== ctx.tripId) {
    mapState = {
      instanceId: ctx.instance.id,
      tripId: ctx.tripId,
      dayKey: dayKey ?? null,
      selectedId: null,
      points: [],
      routeInfo: null,
      loading: true,
      geocoding: false,
      progress: { done: 0, total: 0, failed: 0, phase: 'loading' },
    };
  } else {
    mapState.instanceId = ctx.instance.id;
    mapState.dayKey = dayKey ?? null;
    mapState.selectedId = null;
    mapState.loading = true;
    mapState.geocoding = false;
  }
}

export async function renderMapView(container, ctx, { dayKey = null } = {}) {
  const sameTripShell = mapShellTripId === ctx.tripId && container.querySelector('#trip-map-canvas');

  if (!sameTripShell) {
    resetMapPanel();
    mapShellTripId = ctx.tripId;
  }

  initMapState(ctx, dayKey);

  if (sameTripShell) {
    const main = getTripMainEl(container);
    if (main) {
      main.innerHTML = renderMapContent(ctx).trim();
      main.dataset.mapFiltersBound = '';
    }
  } else {
    container.innerHTML = renderTripShell({
      trip: ctx.trip,
      tripId: ctx.tripId,
      activeTab: 'map',
      members: ctx.live.members,
      bases: resolveTripBases(ctx.live),
      mainHtml: renderMapContent(ctx),
      instanceId: ctx.instance.id,
      tripTitle: ctx.trip.title,
    });

    bindTripShell(container, {
      tripId: ctx.tripId,
      instanceId: ctx.instance.id,
      tripTitle: ctx.trip.title,
    });
  }

  bindMapView(container, ctx);
  paintMap(ctx, { fit: false, force: true });
  await refreshMapData(ctx, container, { fit: true });
  bindMapView(container, ctx);
}

export async function updateMapViewMain(container, ctx, { dayKey = null } = {}) {
  const main = getTripMainEl(container);
  if (!main || mapState.tripId !== ctx.tripId) return;

  if (dayKey !== undefined) {
    mapState.dayKey = dayKey;
    mapState.selectedId = null;
  }

  await refreshMapData(ctx, container, { fit: true });
  bindMapView(container, ctx);
}

export function clearMapViewState() {
  resetMapPanel();
  mapShellTripId = null;
  if (paintThrottleTimer) clearTimeout(paintThrottleTimer);
  paintThrottleTimer = null;
  mapState = {
    instanceId: null,
    tripId: null,
    dayKey: null,
    selectedId: null,
    points: [],
    routeInfo: null,
    loading: false,
    geocoding: false,
    progress: { done: 0, total: 0, failed: 0, phase: 'idle' },
  };
}
